import { useEffect, useState } from "react";
import type { SystemHealth } from "@codestack/shared";
import { Badge, Button } from "@codestack/ui";
import { useApi } from "../lib/useApi";
import { ErrorState, LoadingState } from "../components/States";

const statusTone: Record<SystemHealth["status"], "success" | "warning" | "danger"> = {
  ok: "success",
  degraded: "warning",
  down: "danger",
};

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function HealthPage() {
  const { data, error, loading, reload } = useApi<SystemHealth>("/admin/health");
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = window.setInterval(reload, 30_000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, reload]);

  if (loading && !data) return <LoadingState label="Checking system health…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  const queueTotal = data.queue.queued + data.queue.running + data.queue.failed + data.queue.dead;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-white">System health</h1>
          <p className="text-sm text-ink-400">
            Live status of the API, queue, webhook delivery and Squad connectivity.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-ink-400">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.checked)}
              className="h-3.5 w-3.5 rounded border-white/20 bg-transparent"
            />
            Auto-refresh
          </label>
          <Button variant="secondary" size="sm" onClick={reload} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="admin-card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <span
            className={`h-3 w-3 rounded-full ${
              data.status === "ok"
                ? "bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.6)]"
                : data.status === "degraded"
                  ? "bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.6)]"
                  : "bg-rose-500 shadow-[0_0_12px_rgba(244,63,94,0.6)]"
            }`}
          />
          <div>
            <p className="font-display text-lg font-bold text-white">
              {data.status === "ok"
                ? "All systems operational"
                : data.status === "degraded"
                  ? "Degraded performance"
                  : "Service disruption"}
            </p>
            <p className="text-xs text-ink-500">
              API v{data.version} · uptime {formatUptime(data.uptimeSeconds)}
            </p>
          </div>
        </div>
        <Badge tone={statusTone[data.status]}>{data.status.toUpperCase()}</Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="admin-card p-5">
          <p className="admin-label">Database</p>
          <div className="mt-2 flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                data.database === "ok" ? "bg-emerald-400" : "bg-rose-500"
              }`}
            />
            <p className="font-display text-lg font-bold text-white">
              {data.database === "ok" ? "Connected" : "Unreachable"}
            </p>
          </div>
          <p className="mt-1 text-[11px] text-ink-500">MySQL via Prisma</p>
        </div>

        <div className="admin-card p-5">
          <p className="admin-label">Squad</p>
          <div className="mt-2 flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                !data.squad.configured
                  ? "bg-ink-600"
                  : data.squad.reachable
                    ? "bg-emerald-400"
                    : "bg-rose-500"
              }`}
            />
            <p className="font-display text-lg font-bold text-white">
              {!data.squad.configured
                ? "Not configured"
                : data.squad.reachable
                  ? "Reachable"
                  : "Unreachable"}
            </p>
          </div>
          <p className="mt-1 text-[11px] text-ink-500">
            {data.squad.configured ? "Payment provider" : "Add keys in Settings"}
          </p>
        </div>

        <div className="admin-card p-5">
          <p className="admin-label">Queue</p>
          <p className="admin-value mt-2">{queueTotal}</p>
          <p className="mt-1 text-[11px] text-ink-500">
            {data.queue.queued} queued · {data.queue.running} running
          </p>
        </div>

        <div className="admin-card p-5">
          <p className="admin-label">Webhooks (24h)</p>
          <p className="admin-value mt-2">{data.webhooks.delivered24h}</p>
          <p className="mt-1 text-[11px] text-ink-500">
            {data.webhooks.pending} pending · {data.webhooks.failed} failed
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="admin-card p-5">
          <h2 className="font-display text-sm font-semibold text-white">Job queue</h2>
          <ul className="mt-4 space-y-2">
            {(
              [
                ["Queued", data.queue.queued, "text-sky-300"],
                ["Running", data.queue.running, "text-brand-300"],
                ["Failed (retrying)", data.queue.failed, "text-amber-300"],
                ["Dead", data.queue.dead, "text-rose-300"],
              ] as const
            ).map(([label, value, tone]) => (
              <li
                key={label}
                className="flex items-center justify-between rounded-lg border border-white/[0.06] px-3 py-2"
              >
                <span className="text-xs text-ink-400">{label}</span>
                <span className={`font-mono text-sm ${tone}`}>{value}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="admin-card p-5">
          <h2 className="font-display text-sm font-semibold text-white">Webhook delivery</h2>
          <ul className="mt-4 space-y-2">
            {(
              [
                ["Delivered (24h)", data.webhooks.delivered24h, "text-emerald-300"],
                ["Pending", data.webhooks.pending, "text-sky-300"],
                ["Failed", data.webhooks.failed, "text-rose-300"],
              ] as const
            ).map(([label, value, tone]) => (
              <li
                key={label}
                className="flex items-center justify-between rounded-lg border border-white/[0.06] px-3 py-2"
              >
                <span className="text-xs text-ink-400">{label}</span>
                <span className={`font-mono text-sm ${tone}`}>{value}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[11px] leading-relaxed text-ink-600">
            Failed deliveries are retried with exponential backoff. Platforms must acknowledge
            with a 2xx response to stop retries.
          </p>
        </section>
      </div>
    </div>
  );
}
